import { MATTER_TASK_STATUS_OPTIONS } from "@/lib/data/static/matterStatics";
import type {
  MatterTask,
  MatterTaskComment,
  MatterTaskWithComments,
  MatterUserConnection,
} from "@/lib/types";
import dayjs from "dayjs";

export type MatterTaskWithRoles = MatterTask & {
  assigners: MatterUserConnection[];
  assignees: MatterUserConnection[];
  notifiers: MatterUserConnection[];
  comments: MatterTaskComment[];
};

type TaskViewModelMatter = {
  taskComments?: MatterTaskComment[];
  tasks?: MatterTask[];
  userConnections?: MatterUserConnection[];
};

export const useTasksViewModel = (
  matter?: TaskViewModelMatter,
  tasks?: MatterTaskWithComments[],
) => {
  if (!matter)
    return {
      overdueTasks: [],
      completedTasks: [],
      notCompletedTasks: [],
      allTasks: [],
    };
  const matterTasks: MatterTask[] =
    tasks ?? (Array.isArray(matter.tasks) ? matter.tasks : []);
  const matterUserConnections: MatterUserConnection[] = Array.isArray(
    matter.userConnections,
  )
    ? matter.userConnections
    : [];
  const matterTaskComments: MatterTaskComment[] = Array.isArray(
    matter.taskComments,
  )
    ? matter.taskComments
    : [];
  const tasksWithRolesAndComments: MatterTaskWithRoles[] = matterTasks.map(
    (task) => {
      const assigners = matterUserConnections.filter((role) => {
        return role.taskId === task.id && role.label === "Assigner";
      });
      const assignees = matterUserConnections.filter((role) => {
        return role.taskId === task.id && role.label === "Assignee";
      });
      const notifiers = matterUserConnections.filter((role) => {
        return role.taskId === task.id && role.label === "Notify";
      });
      const comments =
        tasks?.find((paginatedTask) => paginatedTask.id === task.id)
          ?.comments ??
        matterTaskComments.filter((comment) => comment.taskId === task.id);
      return {
        ...task,
        assigners,
        assignees,
        notifiers,
        comments,
      };
    },
  );
  const completeStatuses = ["Completed"];
  const notCompletedStatuses = MATTER_TASK_STATUS_OPTIONS.filter(
    (status) => !completeStatuses.includes(status),
  );
  const overdueTasks = tasksWithRolesAndComments.filter(
    (task) =>
      task.dueDate &&
      dayjs(dayjs().format("YYYY-MM-DD")).isAfter(dayjs(task.dueDate)) &&
      !completeStatuses.includes(task.status),
  );
  const notCompletedTasks = tasksWithRolesAndComments.filter(
    (task) =>
      [...notCompletedStatuses, ""].includes(task.status) &&
      (task.dueDate
        ? !dayjs(task.dueDate).isBefore(dayjs(dayjs().format("YYYY-MM-DD")))
        : true),
  );

  const completedTasks = tasksWithRolesAndComments.filter((task) =>
    completeStatuses.includes(task.status),
  );

  return {
    overdueTasks,
    completedTasks,
    notCompletedTasks,
    allTasks: tasksWithRolesAndComments,
  };
};
